import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UserPlus, Users } from 'lucide-react';

import {
  getTenantAdmins,
  createTenantAdmin,
  assignTenantAdmin,
  removeTenantAdmin,
  getAdminCandidates,
} from '../../../services/tenantService';
import { requestOtp } from '../../../services/authService';
import { parseTableParams } from '../../../utils/Utils';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import PersonCell from '../../../components/ui/PersonCell';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../context/AuthContext';

const Container = styled.div``;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 12px;
`;

const CandidateRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CandidateName = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CandidateMeta = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

export default function TenantAdminTab({ tenantId }) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState({ isOpen: false, row: null });
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    setFetching(true);
    getTenantAdmins(tenantId)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [tenantId, queryParams, refreshTrigger]);

  const handleResendOtp = useCallback(async (row) => {
    const email = row.email;
    setSendingOtp(email);
    try {
      await requestOtp(email);
      toast.success(t('tenant.otpSent'));
    } catch { /* interceptor */ }
    setSendingOtp(null);
  }, [t]);

  const handleRemoveClick = useCallback((row) => {
    setRemoveConfirm({ isOpen: true, row });
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    if (!removeConfirm.row) return;
    setRemoving(true);
    try {
      await removeTenantAdmin(tenantId, removeConfirm.row.userId);
      toast.success(t('tenant.adminRemoved'));
      setRemoveConfirm({ isOpen: false, row: null });
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setRemoving(false);
  }, [removeConfirm.row, tenantId, t]);

  const baseColumns = useMemo(() => [
    {
      name: 'fullName', label: t('employee.fullName'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={value || '-'} profilePicture={row?.profilePicture} />;
        },
      },
    },
    {
      name: 'email', label: t('employee.email'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
  ], [t, data]);

  const actionOptions = useMemo(() => [
    {
      type: 'details',
      label: t('tenant.resendPasswordLink'),
      onClick: handleResendOtp,
    },
    {
      type: 'delete',
      label: t('tenant.removeAdmin'),
      onClick: handleRemoveClick,
      // Hide self-removal — backend rejects it anyway.
      condition: (row) => row.userId !== user?.id,
    },
  ], [t, handleResendOtp, handleRemoveClick, user?.id]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleSave = async (formData) => {
    try {
      const res = await createTenantAdmin(tenantId, formData);
      const resData = res.data?.data;
      if (resData?.email) {
        await requestOtp(resData.email);
      }
      setDrawerOpen(false);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch { return false; }
  };

  const handleAssignSave = async (formData) => {
    // AsyncGenericSelect (single mode) returns just the option id, not the object.
    const userId = formData.userId;
    if (!userId) return false;
    try {
      await assignTenantAdmin(tenantId, userId);
      toast.success(t('tenant.adminAssigned'));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch { return false; }
  };

  const schema = [
    { name: 'firstName', label: t('tenant.adminFirstName'), type: 'text', grid: 6, required: true },
    { name: 'lastName', label: t('tenant.adminLastName'), type: 'text', grid: 6, required: true },
    { name: 'email', label: t('tenant.adminEmail'), type: 'email', grid: 12, required: true },
    { name: 'citizenId', label: t('employee.citizenId'), type: 'text', grid: 6 },
    { name: 'tz', label: t('employee.timezone'), type: 'timezone', grid: 6 },
    { name: 'phoneNumber', label: t('employee.phone'), type: 'phone', grid: 12, dialCodeField: 'areaCode' },
    { name: 'dateOfBirth', label: t('employee.dateOfBirth'), type: 'date', grid: 6 },
    { name: 'employmentDate', label: t('employee.employmentDate'), type: 'date', grid: 6 },
  ];

  // Schema for the "assign existing user" drawer — user picker that searches
  // across all caller-accessible tenants. Backend filters by accessibleTenantIds
  // and excludes users already admin of this tenant.
  const assignSchema = useMemo(() => [
    {
      name: 'userId', label: t('tenant.selectUser'), type: 'asyncSelect', grid: 12, required: true,
      onSearch: async (q) => {
        const res = await getAdminCandidates({ q, excludeTenantId: tenantId });
        const r = res.data?.data;
        const items = Array.isArray(r) ? r : (r?.data || []);
        return items;
      },
      // No onGetById needed — drawer always opens with empty value, never edit mode.
      renderOption: (option) => (
        <CandidateRow>
          <CandidateName>{option.name || option.email}</CandidateName>
          <CandidateMeta>{option.email} · {option.tenantName}</CandidateMeta>
        </CandidateRow>
      ),
    },
  ], [t, tenantId]);

  return (
    <Container>
      <ActionsRow>
        <Button variant="outline" onClick={() => setAssignDrawerOpen(true)}>
          <Users size={16} /> {t('tenant.assignFromExisting')}
        </Button>
        <Button onClick={() => setDrawerOpen(true)}>
          <UserPlus size={16} /> {t('tenant.newAdmin')}
        </Button>
      </ActionsRow>

      {useMemo(() => (
        <DataTable
          title={t('tenant.admins')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('tenant.newAdmin')}
        schema={schema}
        onSave={handleSave}
      />

      <GenericFormDrawer
        open={assignDrawerOpen}
        onClose={() => setAssignDrawerOpen(false)}
        title={t('tenant.assignFromExisting')}
        schema={assignSchema}
        onSave={handleAssignSave}
      />

      <ConfirmDialog
        isOpen={removeConfirm.isOpen}
        title={t('tenant.removeAdminTitle')}
        message={t('tenant.removeAdminMessage', { name: removeConfirm.row?.fullName || removeConfirm.row?.email || '' })}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveConfirm({ isOpen: false, row: null })}
        loading={removing}
      />
    </Container>
  );
}
